import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import config from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]                       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const loadingRef                            = useRef(false);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    const token = getToken();
    console.log('AuthProvider - Token from localStorage:', token ? 'exists' : 'none');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setUser(null);
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  const login = async (credentials) => {
    try {
      console.log('login - Attempt with:', credentials);

      const response = await api.post('/auth/login', credentials);
      const { access_token, refresh_token, user } = response.data;

      console.log('login - Received token:', access_token?.substring(0, 20) + '...');
      console.log('login - User role:', user?.role);

      localStorage.setItem('token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setUser(user);
      setIsAuthenticated(true);

      toast.success('Login successful!');

      // FIX: return role so Login.jsx can navigate to the right place
      return { success: true, role: user.role };

    } catch (error) {
      console.error('login - Error:', error.response?.data);

      let message = 'Login failed';
      if (error.response) {
        message = error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        message = `Cannot connect to server at ${config.apiUrl}. Make sure backend is running.`;
      } else {
        message = error.message;
      }

      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      console.log('Register - Sending data to backend:', userData);
      
      // Make sure we're using the correct endpoint
      const response = await api.post('/auth/register', userData);
      
      console.log('Register - Response:', response.data);
      
      // Check if the response indicates success
      if (response.data.success) {
        toast.success(response.data.message || 'Registration successful!');
        return { success: true, data: response.data };
      } else {
        // Handle case where success is false but we still got a response
        const errorMsg = response.data.error || 'Registration failed';
        toast.error(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error('Register - Error:', error);
      console.error('Register - Error response:', error.response?.data);
      
      // Extract error message from response
      let message = 'Registration failed';
      if (error.response) {
        message = error.response.data?.error || 
                  error.response.data?.message || 
                  `Server error: ${error.response.status}`;
      } else if (error.request) {
        message = 'Cannot connect to server. Please check your connection.';
      } else {
        message = error.message;
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = (showToast = true) => {
    console.log('logout - Logging out');
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
    if (showToast) {
      toast.success('Logged out successfully');
    }
  };

  const refreshToken = async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refresh_token');
      if (!storedRefreshToken) throw new Error('No refresh token');

      const response = await api.post('/auth/refresh', {
        refresh_token: storedRefreshToken
      });

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      return true;

    } catch (error) {
      console.error('Refresh token failed:', error);
      logout(false);
      return false;
    }
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    if (user.role === 'Super Admin') return true;
    if (user.permissions.all) return true;
    return user.permissions[permission] === true;
  };

  /** App modules: pos | inventory | finance | crm | ai_bi */
  const hasModule = (moduleCode) => {
    if (!user) return false;
    if (user.role === 'Super Admin') return true;
    const codes = user.module_codes || [];
    // No selection yet (e.g. fresh trial) — allow all until they configure apps
    if (!codes.length) return true;
    return codes.includes(moduleCode);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      refreshToken();
    }, (config.refreshTokenInterval || 30) * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value = {
    user,
    activeBranch: user?.branch_id || null,
    token: getToken(),
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    refreshToken,
    loadUser,
    hasPermission,
    hasModule
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};